import { Link } from "react-router"
import { sessionApiClient } from "@planningcenter/cc-api-client"
import { useQuery } from "@tanstack/react-query"
import { BlankState, Icon, Skeleton } from "source/shared/components"
import { unlessCommonError } from "source/CommonErrorBoundary"
import { AnnouncementListItem } from "source/announcements/AnnouncementListItem"
import type { Announcement } from "source/announcements/types"

type BlockAttributes = {
  headline?: string
  headline_enabled: boolean
  headline_alignment: "left" | "center" | "right"
  image_display?: "all" | "none"
  only_featured: boolean
  query_params: string
  view_all_params?: string
}

const FIXED_PARAMS = [
  "fields[Announcement]=title,summary,image,featured,link",
  "order=-featured,-show_at",
].join("&")

function useAnnouncements(queryParams: string) {
  const search = [FIXED_PARAMS, queryParams].filter(Boolean).join("&")

  return useQuery({
    queryKey: ["publishing", "announcements-block", search],
    queryFn: (): Promise<{ data: Announcement[] }> =>
      sessionApiClient.get(`/publishing/v2/announcements?${search}`),
    retry: unlessCommonError,
  })
}

export function Announcements({ attributes }: { attributes: BlockAttributes }) {
  const {
    headline_enabled: headlineEnabled,
    headline,
    headline_alignment: headlineAlignment,
    image_display: imageDisplay = "all",
    only_featured: onlyFeatured,
    query_params: queryParams,
    view_all_params: viewAllParams = "",
  } = attributes

  const { data: response, isSuccess, isError } = useAnnouncements(queryParams)
  const announcements = response?.data
  const hasAnnouncements = isSuccess && !!announcements?.length

  return (
    <div css={styles.container} className="page-block">
      {headlineEnabled && headline && (
        <h2
          css={hasAnnouncements && styles.headlineDivider}
          style={{ fontSize: 30, textAlign: headlineAlignment }}
        >
          {headline}
        </h2>
      )}

      <AnnouncementsContent
        showImages={imageDisplay === "all"}
        onlyFeatured={onlyFeatured}
        isSuccess={isSuccess}
        isError={isError}
        announcements={announcements}
      />

      {hasAnnouncements && (
        <ViewAllLink
          alignment={headlineAlignment}
          queryString={viewAllParams}
        />
      )}
    </div>
  )
}

function ViewAllLink({
  alignment,
  queryString,
}: {
  alignment: "left" | "center" | "right"
  queryString: string
}) {
  return (
    <div style={{ textAlign: alignment }}>
      <Link to={{ pathname: "/announcements", search: queryString }}>
        View all
      </Link>
    </div>
  )
}

function AnnouncementsContent({
  showImages,
  onlyFeatured,
  isSuccess,
  isError,
  announcements,
}: {
  showImages: boolean
  onlyFeatured: boolean
  isSuccess: boolean
  isError: boolean
  announcements: Announcement[] | undefined
}) {
  if (isError) return <ErrorState />
  if (!isSuccess) return <LoadingState />
  if (!announcements || announcements.length === 0) {
    return (
      <div className="mt-1">
        <NoAnnouncementsPlaceholder />
      </div>
    )
  }

  return (
    <ListView
      announcements={announcements}
      showImages={showImages}
      onlyFeatured={onlyFeatured}
    />
  )
}

function ListView({
  announcements,
  showImages,
  onlyFeatured,
}: {
  announcements: Announcement[]
  showImages: boolean
  onlyFeatured: boolean
}) {
  return (
    <div className="d-f fd-c" css={styles.listContainer}>
      {announcements.map((announcement) => (
        <AnnouncementListItem
          key={announcement.id}
          announcement={announcement}
          showImage={showImages}
          showFeaturedBadge={!onlyFeatured}
        />
      ))}
    </div>
  )
}

function NoAnnouncementsPlaceholder() {
  return (
    <div className="ta-c c-tint2 fs-2">
      <Icon symbol="general#megaphone" className="mb-1" aria-hidden />
      <div>Looks like there aren&apos;t any announcements available</div>
    </div>
  )
}

function ErrorState() {
  return (
    <BlankState.Root role="alert">
      <BlankState.Icon symbol="general#exclamation-triangle" />
      <BlankState.Content>
        <BlankState.Heading text="Error loading announcements" />
      </BlankState.Content>
    </BlankState.Root>
  )
}

function LoadingState() {
  return <Skeleton.Paragraph />
}

const styles = {
  container: {
    display: "flex",
    flexDirection: "column" as const,
    margin: "0 auto",
    maxWidth: "640px",
    marginBottom: "24px",
    gap: "12px",
    "> h2": {
      marginBottom: "4px",
    },
  },
  headlineDivider: {
    paddingBottom: "16px",
    borderBottom: "1px solid var(--d-border-color)",
  },
  listContainer: {
    "> * ": {
      borderTop: "1px solid var(--d-border-color)",
      paddingTop: "16px",
      marginBottom: "16px",
    },
    "> *:first-child": {
      borderTop: "none",
      paddingTop: 0,
    },
  },
}
